import { toast } from 'react-toastify';
import CopyToClipboardButton from '../components/copy-button';
import RandomImage from '../components/random-image';
import { v4 as uuid } from 'uuid';
import { IoClose } from 'react-icons/io5';

const randomImageStyle = {
  position: 'absolute',
  height: 70,
  right: 0,
  top: 0,
  opacity: 0.3,
  zIndex: -1,
};

const defaultSettings = {
  position: 'bottom-left',
  autoClose: 5000,
  hideProgressBar: false,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true,
  progress: undefined,
  theme: 'colored',
  update: true,
  icon: false,
};

const CloseButton = ({ closeToast }) => (
  <i
    data-testid='close-button'
    className='Toastify__close-button'
    style={{ height: 15, width: 15 }}
    onClick={closeToast}>
    <IoClose />
  </i>
);

const defaultErrorOptions = {
  allowCopy: true,
  closeButton: CloseButton,
  closeOnClick: false,
  autoClose: true,
  ...defaultSettings,
};

const toastTypes = {
  SUCCESS: 'success',
  ERROR: 'error',
  WARNING: 'warning',
  INFO: 'info',
  MESSAGE: 'message',
};

function getTextWidth(text, font) {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  if (context) {
    context.font = font || getComputedStyle(document.body).font;
    return context.measureText(text).width;
  }
  return 0;
}

function getErrorWidth(text, title = null, copyToClipboard) {
  const isArray = Array.isArray(text);
  if (!isArray) {
    return text;
  }
  const maxWidth = 800;
  const width = text.reduce(function (width, errorMessage) {
    const nextWidth = getTextWidth(errorMessage, '20px Roboto sans-serif');
    if (width < nextWidth) {
      return Math.min(nextWidth, maxWidth);
    }
    return width;
  }, 320);
  const titleWidth = title ? getTextWidth(title, '24px sans-serif') : 0;
  return {
    width: `${
      titleWidth > width ? titleWidth : width + (copyToClipboard ? 50 : 0)
    }px`,
  };
}

function getDisplayColor(messages) {
  const hasError = messages.some((m) => m.type === toastTypes.ERROR);
  const hasWarning = messages.some((m) => m.type === toastTypes.WARNING);
  const hasMessage = messages.some((m) => m.type === toastTypes.MESSAGE);
  if (hasError) return 'error';
  if (hasWarning) return 'warning';
  if (hasMessage) return 'info';
  return 'white';
}

function renderMessages(messages, title, copyToClipboard, color, closeToast) {
  return (
    <div className='toastContainer' style={{ overflow: 'hidden !important', position: 'relative' }}>
      <RandomImage style={randomImageStyle} />
      <div>
        <div>
          {title ? (
            <span>
              <h5
                style={{
                  marginBottom: messages.length === 0 ? '0' : '8px',
                  color: `var(--${color})`,
                }}>
                {title}
              </h5>
            </span>
          ) : null}
          <div className="centre-scroll" style={{ maxHeight: 200, overflowY: 'auto', paddingRight: 44 }}>
            {messages.map((e, index) => {
              if (!e.text) return null;
              return (
                <div
                  key={index}
                  style={{ display: 'flex', alignItems: 'flex-start', paddingBottom: 6 }}>
                  <div style={{ marginLeft: 8, whiteSpace: 'pre-wrap' }}>{e.text}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      {copyToClipboard && (
        <div className="copy-toast-button">
          <CopyToClipboardButton
            text={messages.map((m) => `[${m.type}]: ${m.text}`).join('\n')}
            closeToast={closeToast}
          />
        </div>
      )}
    </div>
  );
}

function createToastService() {
  function success(text, toastId = uuid(), options = defaultSettings) {
    return displayToast({
      toastId,
      data: { message: text },
      type: toastTypes.SUCCESS,
      options,
    });
  }

  function error(text, toastId = uuid(), options = defaultErrorOptions) {
    return displayToast({
      toastId,
      data: { message: text },
      type: toastTypes.ERROR,
      options,
    });
  }

  function warn(text, toastId = uuid(), options = defaultErrorOptions, title = null) {
    return displayToast({
      toastId,
      title,
      data: { message: text },
      type: toastTypes.WARNING,
      options,
    });
  }

  function info(text, toastId = uuid(), options = defaultSettings, title = null) {
    return displayToast({
      toastId,
      data: { message: text },
      title,
      type: toastTypes.INFO,
      options,
    });
  }

  function displayToast({ toastId, data, title, type, options = {}, copyToClipboard }) {
    let messages = [];
    let errors = false;
    let header = title || options.title;

    if (typeof data === 'object') {
      const collected = [];

      if (Array.isArray(data)) {
        data.forEach((item) => {
          if (item) collected.push({ type, text: item });
        });
      } else {
        if (data.error) collected.push({ type: toastTypes.ERROR, text: data.error });

        const collectField = (field, msgType) => {
          const val = data[field];
          if (!val) return;
          if (Array.isArray(val)) {
            val.forEach((item) => {
              if (item) collected.push({ type: msgType, text: item });
            });
          } else if (typeof val === 'object') {
            Object.values(val).forEach((v) => {
              if (Array.isArray(v)) {
                v.forEach((item) => {
                  if (item) collected.push({ type: msgType, text: item });
                });
              } else if (v) {
                collected.push({ type: msgType, text: v });
              }
            });
          } else {
            collected.push({ type: msgType, text: val });
          }
        };

        collectField('errors', toastTypes.ERROR);
        collectField('warnings', toastTypes.WARNING);
        collectField('messages', toastTypes.MESSAGE);

        let usedFallback = false;
        if (collected.length === 0 && data.message) {
          usedFallback = true;
          const m = data.message;
          if (Array.isArray(m)) {
            m.forEach((item) => item && collected.push({ type, text: item }));
          } else {
            collected.push({ type, text: m });
          }
        }

        if (!usedFallback && collected.length > 0) {
          header = data.message || header;
        }

        errors = data.success;
      }

      const seen = new Set();
      messages = collected.filter((m) => m.text).filter((m) => {
        if (seen.has(m.text)) return false;
        seen.add(m.text);
        return true;
      });

      if (messages.length === 0) {
        const fallbackText = typeof data === 'object' ? JSON.stringify(data) : data;
        messages = [{ type, text: fallbackText }].filter((m) => m.text);
      }
    } else {
      messages = [{ type, text: data }];
    }

    const width = getErrorWidth(
      messages.slice(0, 5).map((m) => m.text),
      header,
      copyToClipboard
    );
    const autoClose = errors && messages.length > 1 ? false : options.autoClose;
    const fullOptions = {
      ...defaultErrorOptions,
      ...options,
      copyToClipboard,
      style: width,
      autoClose: !autoClose ? autoClose : autoClose || defaultErrorOptions.autoClose,
      isLoading: false,
    };

    if (type) {
      fullOptions.type = type;
    }

    const closeThisToast = () => {
      try {
        if (toast && toast.dismiss) {
          toast.dismiss(toastId);
        }
      } catch (e) {
        /* ignore */
      }
    };

    const isActive = toast.isActive ? toast.isActive(toastId) : false;
    if (isActive) {
      return toast.update(toastId, {
        ...fullOptions,
        render: renderMessages(messages, header, copyToClipboard, getDisplayColor(messages), closeThisToast),
      });
    }
    const toaster = renderMessages(messages, header, copyToClipboard, getDisplayColor(messages), closeThisToast);
    return toast(toaster, {
      ...defaultSettings,
      toastId,
      ...fullOptions,
      type,
    });
  }

  return {
    success,
    warn,
    error,
    info,
    displayToast,
    toastTypes,
  };
}

export { createToastService };

const toastService = createToastService();
export default toastService;
